/**
 * Conversión de un importe (UF / US$ / moneda local) a moneda local.
 *
 * Extraído tal cual del callsite inline que vivía en `src/pages/ER/index.js` para que
 * el ER v1 y el ER v2 compartan UNA sola implementación. Es una factory: el estado
 * sobre el que la versión inline cerraba (`costosGestionData`, `getUFValue`,
 * `getDolarValue`, el año por defecto) ahora entra por parámetro, así la función
 * devuelta es pura respecto de sus argumentos.
 *
 * Regla de fecha (la que decide el tipo de cambio a aplicar), en orden:
 *   1. `fechaUsadaOverride` si viene.
 *   2. `fecha_factura` del registro de `costos_gestion_contenidos` que matchee
 *      negocio + mes + año.
 *   3. Fallback por moneda: UF → último día del mes; US$ → día 1 del mes.
 *   4. Hoy.
 *
 * @param {object} deps
 * @param {Array} deps.costosGestionData - Filas de costos_gestion_contenidos (aportan fecha_factura)
 * @param {Function} deps.getUFValue - (fecha) => valor UF
 * @param {Function} deps.getDolarValue - (fecha) => valor dólar
 * @param {number} deps.anoPorDefecto - Año usado cuando el caller no pasa `ano`
 * @returns {Function} (valor, monedaOriginal, pais, negocioId, mes, ano, fechaUsadaOverride) => number
 */
export const crearConvertirAMonedaLocal = ({ costosGestionData, getUFValue, getDolarValue, anoPorDefecto }) => {
  const buscarFechaFactura = (negocioId, mes, ano) => {
    if (!negocioId || !mes || !ano) return null;
    const registro = (costosGestionData || []).find(
      (c) =>
        parseInt(c.negocio_id) === parseInt(negocioId) &&
        parseInt(c.mes) === mes &&
        parseInt(c.ano) === ano &&
        c.fecha_factura
    );
    return registro?.fecha_factura ? registro.fecha_factura.split("T")[0] : null;
  };

  return (
    valor,
    monedaOriginal,
    pais,
    negocioId = null,
    mes = null,
    ano = anoPorDefecto,
    fechaUsadaOverride = null
  ) => {
    if (!valor) return 0;
    const num = parseFloat(valor) || 0;
    const monedaUpper = monedaOriginal?.toUpperCase() || "";

    // UF solo Chile
    if (pais === "Chile" && monedaUpper.includes("UF")) {
      let fechaUsada = fechaUsadaOverride || buscarFechaFactura(negocioId, mes, ano);
      if (!fechaUsada && mes && ano) {
        const ultimoDia = new Date(ano, mes, 0);
        fechaUsada = ultimoDia.toISOString().split("T")[0];
      }
      if (!fechaUsada) fechaUsada = new Date().toISOString().split("T")[0];
      return Math.round(num * getUFValue(fechaUsada));
    }

    // US$ → convertir a local usando dólar observado
    if (monedaUpper === "US$" || monedaUpper === "USD") {
      let fechaUsada = fechaUsadaOverride || buscarFechaFactura(negocioId, mes, ano);
      if (!fechaUsada && mes) {
        fechaUsada = `${ano}-${String(mes).padStart(2, "0")}-01`; // día 1 si no hay fecha
      }
      if (!fechaUsada) fechaUsada = new Date().toISOString().split("T")[0];
      return Math.round(num * getDolarValue(fechaUsada));
    }

    // Moneda local o cualquier otra → directo
    return Math.round(num);
  };
};
